import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';
const cp=x=>x==null?x:JSON.parse(JSON.stringify(x));

const SEVERITY=Object.freeze({INFO:0,REVIEW:1,BLOCK:2});
function sev(x){return Object.prototype.hasOwnProperty.call(SEVERITY,x)?SEVERITY[x]:SEVERITY.REVIEW;}

function classify(issue={}){
  const type=issue.type||issue.reason||'UNKNOWN';
  const required=!!issue.required;
  const critical=!!issue.critical;

  if(critical) return Object.freeze({action:'BLOCK',severity:'BLOCK',reason:type});
  if(required) return Object.freeze({action:'REVIEW',severity:'REVIEW',reason:type});

  const blockTypes=new Set(['DATA_CORRUPTION','OWNERSHIP_VIOLATION','SCHEMA_UNSUPPORTED','CONTRACT_BREACH']);
  const reviewTypes=new Set(['PROVIDER_ERROR','STALE_CRITICAL_DATA','ENGINE_ERROR','PERSIST_FAILURE','INTEGRITY_WEAK','UNRESOLVED_CONFLICT']);
  if(blockTypes.has(type)) return Object.freeze({action:'BLOCK',severity:'BLOCK',reason:type});
  if(reviewTypes.has(type)) return Object.freeze({action:'REVIEW',severity:'REVIEW',reason:type});
  return Object.freeze({action:'DEGRADE',severity:'INFO',reason:type});
}

function contain(input={}){
  const issues=Array.isArray(input.issues)?input.issues:[];
  const features=cp(input.features||{});
  const decisions=[];
  let strongest='INFO';

  for(const issue of issues){
    const d=classify(issue);
    decisions.push({...d,source:issue.source||null,feature:issue.feature||null});
    if(sev(d.severity)>sev(strongest)) strongest=d.severity;

    if(issue.feature && Object.prototype.hasOwnProperty.call(features,issue.feature)){
      if(d.action==='BLOCK') features[issue.feature]={...features[issue.feature],state:'UNAVAILABLE',confidence:'NONE'};
      else if(d.action==='REVIEW') features[issue.feature]={...features[issue.feature],state:'REVIEW',confidence:'LOW'};
      else features[issue.feature]={...features[issue.feature],state:'DEGRADED',confidence:'REDUCED'};
    }
  }

  const globalStatus=strongest==='BLOCK'?'BLOCK':strongest==='REVIEW'?'REVIEW':'READY_WITH_DEGRADATION';
  return Object.freeze({
    version:VERSION,
    status:issues.length?globalStatus:'READY',
    features:Object.freeze(features),
    decisions:Object.freeze(decisions),
    falseConfidenceGuard:issues.length>0
  });
}

function guardOutput(output={},containment={}){
  const o=cp(output);
  if(containment.status==='BLOCK'){
    o.readiness='BLOCKED';
    o.confidence='NONE';
  }else if(containment.status==='REVIEW'){
    if(!o.readiness || o.readiness==='READY') o.readiness='REVIEW';
    if(!o.confidence || o.confidence==='HIGH') o.confidence='LOW';
  }else if(containment.status==='READY_WITH_DEGRADATION'){
    if(!o.confidence || o.confidence==='HIGH') o.confidence='REDUCED';
  }
  o.degraded=containment.status!=='READY';
  return Object.freeze(o);
}

function mergeIssues(...groups){
  return Object.freeze(groups.flatMap(g=>Array.isArray(g)?g:[]).map(cp));
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  ok('optional unknown degrades',classify({type:'OPTIONAL_MISS'}).action==='DEGRADE');
  ok('required issue reviews',classify({type:'ENGINE_ERROR',required:true}).action==='REVIEW');
  ok('critical issue blocks',classify({type:'ANY',critical:true}).action==='BLOCK');
  ok('ownership violation blocks',classify({type:'OWNERSHIP_VIOLATION'}).action==='BLOCK');
  ok('provider error reviews',classify({type:'PROVIDER_ERROR'}).action==='REVIEW');

  const ready=contain({issues:[],features:{news:{state:'OK'}}});
  ok('no issues ready',ready.status==='READY'&&ready.falseConfidenceGuard===false);

  const degraded=contain({
    issues:[{type:'OPTIONAL_MISS',feature:'news'}],
    features:{news:{state:'OK'},price:{state:'OK'}}
  });
  ok('optional miss keeps system available',degraded.status==='READY_WITH_DEGRADATION');
  ok('affected feature marked degraded',degraded.features.news.state==='DEGRADED');
  ok('unaffected feature preserved',degraded.features.price.state==='OK');

  const review=contain({
    issues:[{type:'PROVIDER_ERROR',feature:'calendar'}],
    features:{calendar:{state:'OK'}}
  });
  ok('provider error produces review',review.status==='REVIEW');
  ok('review lowers feature confidence',review.features.calendar.confidence==='LOW');

  const block=contain({
    issues:[{type:'DATA_CORRUPTION',feature:'researchState'}],
    features:{researchState:{state:'OK'}}
  });
  ok('data corruption blocks',block.status==='BLOCK');
  ok('blocked feature unavailable',block.features.researchState.state==='UNAVAILABLE');

  const gr=guardOutput({readiness:'READY',confidence:'HIGH'},review);
  ok('review guard removes READY',gr.readiness==='REVIEW');
  ok('review guard lowers confidence',gr.confidence==='LOW');

  const gb=guardOutput({readiness:'READY',confidence:'HIGH'},block);
  ok('block guard blocks readiness',gb.readiness==='BLOCKED');
  ok('block guard zeroes confidence',gb.confidence==='NONE');

  const gd=guardOutput({confidence:'HIGH'},degraded);
  ok('degraded guard reduces confidence',gd.confidence==='REDUCED');

  const merged=mergeIssues([{type:'A'}],null,[{type:'B'}]);
  ok('issue merge is deterministic',merged.length===2&&merged[0].type==='A'&&merged[1].type==='B');

  const api={VERSION,classify,contain,guardOutput,mergeIssues};
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestResearchFailureContainmentEngine=Object.freeze({VERSION,classify,contain,guardOutput,mergeIssues,selfTest});
})(engineContext);